import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../hooks/useIsMobile';
import './StudentLayout.css';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  BottomNavigation,
  BottomNavigationAction,
  Paper
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ClassIcon from '@mui/icons-material/Class';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';
import { useNotifications } from '../hooks/useNotifications';
import NotificationPanel from './NotificationPanel';

const StudentLayout: React.FC = () => {
  const { student, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/student/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleOpenPasswordDialog = () => {
    handleClose();
    setPasswordDialogOpen(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };
  
  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordError('');
    setPasswordSuccess('');
  };
  
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('studentPortal.password.allFieldsRequired'));
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError(t('studentPortal.password.minLength'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError(t('studentPortal.password.mismatch'));
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_BASE_URL}/api/student/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setPasswordSuccess(t('studentPortal.password.success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        handleClosePasswordDialog();
      }, 2000);
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t('studentPortal.password.failed');
      setPasswordError(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  const menuItems = [
    { text: t('sidebar.dashboard'), icon: <DashboardIcon />, path: '/student/dashboard' },
    { text: t('sidebar.myClasses'), icon: <ClassIcon />, path: '/student/classes' },
    { text: t('sidebar.myAssignments'), icon: <AssignmentIcon />, path: '/student/assignments' },
  ];

  const drawer = (
    <div className="student-drawer">
      <div className="student-drawer-header">
        <SchoolIcon sx={{ fontSize: 32, color: '#1976d2' }} />
        <Typography variant="h6">{t('studentLogin.title')}</Typography>
      </div>
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
            selected={location.pathname === item.path}
            className={location.pathname === item.path ? 'selected' : ''}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  const bottomNavItems = [
    { label: t('sidebar.dashboard'), icon: <DashboardIcon />, path: '/student/dashboard' },
    { label: t('sidebar.myClasses'), icon: <ClassIcon />, path: '/student/classes' },
    { label: t('sidebar.myAssignments'), icon: <AssignmentIcon />, path: '/student/assignments' },
  ];

  const currentBottomNavIndex = bottomNavItems.findIndex(item => location.pathname.startsWith(item.path));

  return (
    <div className="student-layout">
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: isMobile ? '56px' : '64px' }}>
          <SchoolIcon sx={{ mr: 1.5 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: isMobile ? '1rem' : '1.25rem' }}>
            {t('studentLogin.title')}
          </Typography>

          {student && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}>
              {!isMobile && (
                <Typography variant="body1">
                  {student.first_name} {student.last_name}
                </Typography>
              )}
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  color="inherit"
                  onClick={() => setNotifOpen((prev) => !prev)}
                  aria-label={t('notifications.bellLabel', { count: unreadCount, defaultValue: `Notifications - ${unreadCount} unread` })}
                  aria-expanded={notifOpen}
                  aria-haspopup="dialog"
                >
                  <Badge badgeContent={unreadCount} color="error" max={99}>
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                {notifOpen && (
                  <NotificationPanel
                    notifications={notifications}
                    unreadCount={unreadCount}
                    onMarkAsRead={markAsRead}
                    onMarkAllAsRead={markAllAsRead}
                    onDelete={deleteNotification}
                    onClose={() => setNotifOpen(false)}
                  />
                )}
              </Box>
              <IconButton
                size="large"
                onClick={handleMenu}
                color="inherit"
                aria-label={`Account menu for ${student.first_name} ${student.last_name}`}
                aria-expanded={Boolean(anchorEl)}
                aria-haspopup="true"
              >
                <AccountCircle />
              </IconButton>
            </Box>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleOpenPasswordDialog}>
              <LockIcon sx={{ mr: 1, fontSize: 20 }} />
              {t('studentPortal.changePassword')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>{t('header.signOut')}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{t('studentPortal.password.title')}</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label={t('studentPortal.password.current')}
            type="password"
            fullWidth
            variant="outlined"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={changingPassword}
            sx={{ mb: 2 }}
            aria-label={t('studentPortal.password.current')}
          />
          <TextField
            margin="dense"
            label={t('studentPortal.password.new')}
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={changingPassword}
            sx={{ mb: 2 }}
            aria-label={t('studentPortal.password.new')}
          />
          <TextField
            margin="dense"
            label={t('studentPortal.password.confirm')}
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={changingPassword}
            aria-label={t('studentPortal.password.confirm')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={changingPassword}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained" 
            disabled={changingPassword}
          >
            {changingPassword ? (
              <CircularProgress size={24} />
            ) : (
              t('studentPortal.password.change')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', marginTop: isMobile ? '56px' : '64px' }}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <Box
            component="nav"
            sx={{ width: 240, flexShrink: 0 }}
          >
            <Drawer
              variant="permanent"
              sx={{
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240, marginTop: '64px' },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: isMobile ? '100%' : 'calc(100% - 240px)',
            overflow: 'auto',
            pb: isMobile ? '80px' : 0,
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          elevation={8}
        >
          <BottomNavigation
            showLabels
            value={currentBottomNavIndex >= 0 ? currentBottomNavIndex : false}
            onChange={(_event, newValue) => {
              const item = bottomNavItems[newValue];
              if (item.path) {
                navigate(item.path);
              }
            }}
            sx={{
              height: 64,
              bgcolor: 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(10px)',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                px: 2,
                color: '#6a7280',
                '&.Mui-selected': {
                  color: '#1976d2',
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem',
                  fontWeight: 700,
                },
              },
            }}
          >
            {bottomNavItems.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                icon={item.icon}
                aria-label={item.label}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </div>
  );
};

export default StudentLayout;

